import {
  ClientPortalAvailableDefaultLanguageEnum,
  CustomDocumentTypeModel,
  EntityTypeEnum,
} from '../../../shared/models';

export type DefaultCustomDocumentTypeConfig = Pick<
  CustomDocumentTypeModel,
  | 'label'
  | 'key'
  | 'isInternal'
  | 'targetEntities'
  | 'analysisParameters'
  | 'automaticApproval'
  | 'automaticRejection'
  | 'fraudAnalysis'
>;

// @WARN Do not add new default document type until the breaking changes of check subtype in E-5794 is completed
// This list is used to determine if a custom document type is previously default one to still expose the legacy
// check subtype on the external-api
// @TODO - E-5794 - Remove this comment

export const DEFAULT_CUSTOM_DOCUMENT_TYPES: DefaultCustomDocumentTypeConfig[] =
  [
    {
      label: 'Articles of association',
      key: 'articles_of_association',
      targetEntities: [EntityTypeEnum.company],
    },
    {
      label: 'Registration certificate',
      key: 'registration_certificate',
      targetEntities: [EntityTypeEnum.company],
    },
    {
      label: 'UBO registry extract',
      key: 'ubo_registry_extract',
      targetEntities: [EntityTypeEnum.company],
    },
    {
      label: 'Financial statements',
      key: 'financial_statements',
      targetEntities: [EntityTypeEnum.company],
    },
    {
      label: 'Proof of insurance',
      key: 'proof_of_insurance',
      targetEntities: [EntityTypeEnum.company],
    },
    {
      label: 'Identity document',
      key: 'id_document',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Driving license',
      key: 'driving_license',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Proof of address',
      key: 'proof_of_address',
      targetEntities: [EntityTypeEnum.company, EntityTypeEnum.individual],
    },
    {
      label: 'Visa',
      key: 'visa',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Social security card',
      key: 'social_security_card',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Birth certificate',
      key: 'birth_certificate',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Vehicle registration certificate',
      key: 'vehicle_registration_certificate',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Bank details',
      key: 'bank_details',
      targetEntities: [EntityTypeEnum.company, EntityTypeEnum.individual],
    },
    {
      label: 'Criminal record extract',
      key: 'criminal_record_extract',
      targetEntities: [EntityTypeEnum.individual],
    },
    {
      label: 'Tax notice',
      key: 'tax_notice',
      targetEntities: [EntityTypeEnum.individual],
    },
  ].map((documentType) => ({
    // Default values
    isInternal: false,
    analysisParameters: null,
    automaticApproval: false,
    automaticRejection: false,
    fraudAnalysis: null,

    ...documentType,
  }));

export const DEFAULT_CUSTOM_DOCUMENT_TYPES_KEYS =
  DEFAULT_CUSTOM_DOCUMENT_TYPES.map((documentType) => documentType.key);

/**
 * In Dotfile history, we initially had a pre-defined static list of "default" document type (via an enum) and "custom"
 * document type that can be added per workspace, store in the CustomDocumentType. Now that both list have been merged,
 * there are still some behaviors related to the "default" document type that needs to be maintained for backward compatibility.
 *
 * This function allow to detect if a document type was a default or not based on the key
 *
 * @param documentTypeKey
 * @returns
 */
export const isDefaultDocumentTypeKey = (documentTypeKey: string): boolean =>
  DEFAULT_CUSTOM_DOCUMENT_TYPES_KEYS.includes(documentTypeKey);

/**
 * This contains the default translation for the default document type label for each supported language of the client portal
 * that were previously stored in assets
 *
 * Having this DEFAULT_CUSTOM_DOCUMENT_TYPES_TRANSLATED_LABEL allow to have a default translation for all language even
 * without an explicit wording
 */
export const DEFAULT_CUSTOM_DOCUMENT_TYPES_TRANSLATED_LABEL: Record<
  string,
  Record<string, string>
> = {
  [ClientPortalAvailableDefaultLanguageEnum.de]: {
    articles_of_association: 'Gesellschaftsvertrag',
    registration_certificate: 'Registrierungsbescheinigung',
    ubo_registry_extract: 'UBO-Registerauszug',
    financial_statements: 'Finanzberichte',
    bank_details: 'Bankdetails',
    id_document: 'Identitätsdokument',
    driving_license: 'Führerschein',
    proof_of_address: 'Adressnachweis',
    proof_of_insurance: 'Versicherungsnachweis',
    visa: 'Visum',
    social_security_card: 'Sozialversicherungsausweis',
    birth_certificate: 'Geburtsurkunde',
    vehicle_registration_certificate: 'Fahrzeugschein',
    criminal_record_extract: 'Auszug aus dem Strafregister',
    tax_notice: 'Steuerbescheid',
  },
  [ClientPortalAvailableDefaultLanguageEnum.en]: {
    articles_of_association: 'Articles of association',
    registration_certificate: 'Registration certificate',
    ubo_registry_extract: 'UBO registry extract',
    financial_statements: 'Financial statements',
    bank_details: 'Bank details',
    id_document: 'Identity document',
    driving_license: 'Driving license',
    proof_of_address: 'Proof of address',
    proof_of_insurance: 'Proof of insurance',
    visa: 'Visa',
    social_security_card: 'Social security card',
    birth_certificate: 'Birth certificate',
    vehicle_registration_certificate: 'Vehicle registration certificate',
    criminal_record_extract: 'Criminal record extract',
    tax_notice: 'Tax notice',
  },
  [ClientPortalAvailableDefaultLanguageEnum.es]: {
    articles_of_association: 'Estatutos',
    registration_certificate: 'Certificado de registro',
    ubo_registry_extract: 'Extracto del registro UBO',
    financial_statements: 'Estados financieros',
    bank_details: 'Datos bancarios',
    id_document: 'Documento de identidad',
    driving_license: 'Licencia de conducir',
    proof_of_address: 'Comprobante de domicilio',
    proof_of_insurance: 'Comprobante de seguro',
    visa: 'Visa',
    social_security_card: 'Tarjeta de seguridad social',
    birth_certificate: 'Certificado de nacimiento',
    vehicle_registration_certificate: 'Certificado de registro del vehículo',
    criminal_record_extract: 'Extracto del historial criminal',
    tax_notice: 'Aviso de impuestos',
  },
  [ClientPortalAvailableDefaultLanguageEnum.fr]: {
    articles_of_association: "Statuts de l'association",
    registration_certificate: "Certificat d'enregistrement",
    ubo_registry_extract: 'Extrait du registre UBO',
    financial_statements: 'États financiers',
    bank_details: 'Coordonnées bancaires',
    id_document: "Document d'identité",
    driving_license: 'Permis de conduire',
    proof_of_address: 'Justificatif de domicile',
    proof_of_insurance: "Justificatif d'assurance",
    visa: 'Visa',
    social_security_card: 'Carte de sécurité sociale',
    birth_certificate: 'Acte de naissance',
    vehicle_registration_certificate:
      "Certificat d'immatriculation du véhicule",
    criminal_record_extract: 'Extrait de casier judiciaire',
    tax_notice: "Avis d'imposition",
  },
  [ClientPortalAvailableDefaultLanguageEnum.it]: {
    articles_of_association: 'Statuto',
    registration_certificate: 'Certificato di registrazione',
    ubo_registry_extract: 'Estratto del registro UBO',
    financial_statements: 'Bilanci finanziari',
    bank_details: 'Dati bancari',
    id_document: "Documento d'identità",
    driving_license: 'Patente di guida',
    proof_of_address: 'Prova di indirizzo',
    proof_of_insurance: 'Prova di assicurazione',
    visa: 'Visto',
    social_security_card: 'Tessera di sicurezza sociale',
    birth_certificate: 'Certificato di nascita',
    vehicle_registration_certificate:
      'Certificato di immatricolazione del veicolo',
    criminal_record_extract: 'Estratto del casellario giudiziale',
    tax_notice: 'Avviso fiscale',
  },
  [ClientPortalAvailableDefaultLanguageEnum.nl]: {
    articles_of_association: 'Statuten',
    registration_certificate: 'Registratiecertificaat',
    ubo_registry_extract: 'UBO register uittreksel',
    financial_statements: 'Jaarrekeningen',
    bank_details: 'Bankgegevens',
    id_document: 'Identiteitsdocument',
    driving_license: 'Rijbewijs',
    proof_of_address: 'Bewijs van adres',
    proof_of_insurance: 'Verzekeringsbewijs',
    visa: 'Visum',
    social_security_card: 'Socialezekerheidskaart',
    birth_certificate: 'Geboorteakte',
    vehicle_registration_certificate: 'Voertuigregistratiecertificaat',
    criminal_record_extract: 'Uittreksel strafregister',
    tax_notice: 'Belastingaanslag',
  },
  [ClientPortalAvailableDefaultLanguageEnum.pl]: {
    articles_of_association: 'Statut',
    registration_certificate: 'Certyfikat rejestracji',
    ubo_registry_extract: 'Wyciąg z rejestru UBO',
    financial_statements: 'Sprawozdania finansowe',
    bank_details: 'Dane bankowe',
    id_document: 'Dokument tożsamości',
    driving_license: 'Prawo jazdy',
    proof_of_address: 'Potwierdzenie adresu',
    proof_of_insurance: 'Potwierdzenie ubezpieczenia',
    visa: 'Wiza',
    social_security_card: 'Karta ubezpieczenia społecznego',
    birth_certificate: 'Akt urodzenia',
    vehicle_registration_certificate: 'Certyfikat rejestracji pojazdu',
    criminal_record_extract: 'Wyciąg z rejestru karnego',
    tax_notice: 'Nota podatkowa',
  },
  [ClientPortalAvailableDefaultLanguageEnum.pt]: {
    articles_of_association: 'Estatutos',
    registration_certificate: 'Certificado de registro',
    ubo_registry_extract: 'Extrato do registro UBO',
    financial_statements: 'Demonstrações financeiras',
    bank_details: 'Dados bancários',
    id_document: 'Documento de identidade',
    driving_license: 'Carteira de motorista',
    proof_of_address: 'Comprovante de endereço',
    proof_of_insurance: 'Comprovante de seguro',
    visa: 'Visto',
    social_security_card: 'Cartão de segurança social',
    birth_certificate: 'Certidão de nascimento',
    vehicle_registration_certificate: 'Certificado de registro de veículo',
    criminal_record_extract: 'Extrato de antecedentes criminais',
    tax_notice: 'Aviso fiscal',
  },
} satisfies Record<
  ClientPortalAvailableDefaultLanguageEnum,
  Record<string, string>
>;
